/**
 * Admin: Smart Portfolios. Build a portfolio, propose weights as a version and approve it (the first
 * approval publishes it), set its lock terms and limits, pause new investments, rebalance (dry run
 * first), retire, and watch rebalance runs.
 */
import { useMemo, useState } from "react";
import {
  D,
  equalWeights,
  fmt,
  fmtDate,
  fmtDateTime,
  fmtUsd,
  LOCK_DAYS,
  lockLabel,
  PORTFOLIO_CATEGORIES,
  weightsProblem,
  type LockTerm,
  type PortfolioComponent,
} from "@platform/shared";
import { InstrumentLabel } from "../components/InstrumentIcon";
import { cx, Modal, Spinner } from "../components/ui";
import { NumInput } from "../components/NumInput";
import { api, ApiError } from "../lib/api";
import { useLoad } from "../stores/account";
import { useInstruments } from "../stores/market";
import { NavChart, RiskChip } from "./portfolios/kit";

interface Version {
  id: string;
  version: number;
  status: "proposed" | "approved" | "rejected" | "superseded";
  effective: "now" | "next_rebalance";
  components: PortfolioComponent[];
  note: string;
  proposedBy: string;
  proposedById: string;
  proposedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  effectiveAt: string | null;
}

interface AdminPortfolio {
  id: string;
  slug: string;
  name: string;
  category: string;
  tagline: string;
  description: string;
  strategy: string;
  imageUrl: string | null;
  benchmarkSymbol: string | null;
  minInvestment: string;
  defaultStopLossPct: string;
  schedule: string;
  scheduleDay: number;
  driftPp: string;
  status: string;
  riskScore: number | null;
  currentVersionId: string | null;
  nextRebalanceAt: string | null;
  lastRebalancedAt: string | null;
  publishedAt: string | null;
  retire: {
    requestedBy: string;
    requestedById: string;
    requestedAt: string;
    noticeDays: number;
    reason: string;
    retireAt: string | null;
  } | null;
  investors: number;
  invested: string;
  inflow30d: string;
  outflow30d: string;
  versions: Version[];
  lockTerms: LockTerm[];
  coolingOffDays: number;
  bonusCap: string;
  maxInvestment: string;
  aumCap: string | null;
  locks: {
    byTerm: { days: number; investments: number; invested: string }[];
    dueIn30d: { investmentId: string; userId: string; days: number; lockedUntil: string; invested: string; bonus: string }[];
    bonusesPaid30d: string;
    bonusesPending: string;
    earlyExits30d: number;
    exitFees30d: string;
  };
}

interface Run {
  id: string;
  trigger: string;
  status: string;
  version: number;
  scopeInvestmentId: string | null;
  startedAt: string;
  finishedAt: string | null;
  stats: {
    investments?: number;
    legs?: number;
    done?: number;
    deferred?: number;
    skipped?: number;
    failed?: number;
  };
  deferredNow: number;
  actor: string | null;
}

interface DryRun {
  investments: {
    investmentId: string;
    equity: string;
    legs: { symbol: string; action: string; notional: string }[];
  }[];
  bySymbol: { symbol: string; buy: string; sell: string; net: string }[];
}

const STATUS_TONE: Record<string, string> = {
  draft: "bg-raised text-muted",
  live: "bg-up-bg text-up",
  closed_to_new: "bg-warn-bg text-warn",
  retiring: "bg-down-bg text-down",
  retired: "bg-raised text-muted",
  proposed: "bg-warn-bg text-warn",
  approved: "bg-up-bg text-up",
  rejected: "bg-raised text-muted",
  superseded: "bg-raised text-muted",
  completed: "bg-up-bg text-up",
  partial: "bg-warn-bg text-warn",
  failed: "bg-down-bg text-down",
  aborted: "bg-down-bg text-down",
  running: "bg-raised text-muted",
};
const STATUS_TEXT: Record<string, string> = {
  closed_to_new: "paused",
  next_rebalance: "at next rebalance",
};

function Pill({ s }: { s: string }) {
  return (
    <span
      className={cx(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap",
        STATUS_TONE[s] ?? "bg-raised text-muted",
      )}
    >
      {STATUS_TEXT[s] ?? s.replace(/_/g, " ")}
    </span>
  );
}

const input =
  "h-9 w-full rounded-lg border border-line bg-bg px-2.5 text-[13px] outline-none focus:border-line-strong";
const btn =
  "h-9 rounded-lg border border-line-strong px-3.5 text-[13px] font-semibold hover:bg-raised disabled:opacity-50";
const primary =
  "h-9 rounded-lg bg-accent px-3.5 text-[13px] font-semibold text-white disabled:opacity-50";

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      className={cx("flex flex-col gap-1 text-[12px] text-muted", className)}
    >
      {label}
      {children}
    </label>
  );
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);

// ---------- lock terms and limits ----------

const bpsToPct = (b: number) => String(b / 100);
const pctToBps = (v: string) => Math.round(Number(v || "0") * 100);

function LockLimits({ p, act, busy }: { p: AdminPortfolio; act: (f: () => Promise<unknown>, ok?: string) => Promise<boolean>; busy: boolean }) {
  const initial = () =>
    Object.fromEntries(
      LOCK_DAYS.filter((d) => d > 0).map((d) => {
        const t = p.lockTerms.find((x) => x.days === d);
        return [d, { on: !!t, bonus: bpsToPct(t?.bonusBps ?? 0), fee: bpsToPct(t?.exitFeeBps ?? 0) }];
      }),
    ) as Record<number, { on: boolean; bonus: string; fee: string }>;
  const [terms, setTerms] = useState(initial);
  const [cool, setCool] = useState(String(p.coolingOffDays));
  const [cap, setCap] = useState(p.bonusCap);
  const [max, setMax] = useState(p.maxInvestment);
  const [aum, setAum] = useState(p.aumCap ?? "");
  const total = p.locks.byTerm.reduce((a, t) => a.add(t.invested), D.ZERO);
  const save = () =>
    act(
      () =>
        api.patch(`/api/admin/portfolios/${p.id}`, {
          lockTerms: Object.entries(terms)
            .filter(([, t]) => t.on)
            .map(([d, t]) => ({ days: Number(d), bonusBps: pctToBps(t.bonus), exitFeeBps: pctToBps(t.fee) })),
          coolingOffDays: Number(cool),
          bonusCap: cap || "0",
          maxInvestment: max,
          aumCap: aum.trim() === "" ? null : aum,
        }),
      "Lock terms and limits saved",
    );
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="m-0 text-[14px] font-semibold">Lock terms and limits</h4>
        <span className="text-[12px] text-muted">Changes apply to new investments and new terms; running locks keep their rates</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[560px] border-collapse text-[13px]">
          <thead>
            <tr className="bg-raised text-left text-[11.5px] text-muted">
              <th className="px-3 py-2 font-medium">Term</th>
              <th className="px-3 py-2 font-medium">Offered</th>
              <th className="px-3 py-2 font-medium">Bonus at the end (%)</th>
              <th className="px-3 py-2 font-medium">Early-exit fee (%)</th>
              <th className="px-3 py-2 text-right font-medium">Running now</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-line">
              <td className="px-3 py-2 font-semibold">Flexible</td>
              <td className="px-3 py-2 text-muted">Always</td>
              <td className="px-3 py-2 text-muted">—</td>
              <td className="px-3 py-2 text-muted">—</td>
              <td className="num px-3 py-2 text-right">{fmtUsd(p.locks.byTerm.find((t) => t.days === 0)?.invested ?? "0")}</td>
            </tr>
            {LOCK_DAYS.filter((d) => d > 0).map((d) => {
              const t = terms[d]!;
              const run = p.locks.byTerm.find((x) => x.days === d);
              const set = (patch: Partial<typeof t>) => setTerms({ ...terms, [d]: { ...t, ...patch } });
              return (
                <tr key={d} className="border-t border-line">
                  <td className="px-3 py-2 font-semibold">{lockLabel(d)}</td>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={t.on} onChange={(e) => set({ on: e.target.checked })} aria-label={`Offer ${lockLabel(d)}`} className="size-4 accent-[var(--accent)]" />
                  </td>
                  <td className="px-3 py-2">
                    <NumInput className={cx(input, "w-24")} value={t.bonus} disabled={!t.on} onValue={(v) => set({ bonus: v })} />
                  </td>
                  <td className="px-3 py-2">
                    <NumInput className={cx(input, "w-24")} value={t.fee} disabled={!t.on} onValue={(v) => set({ fee: v })} />
                  </td>
                  <td className="num px-3 py-2 text-right">
                    {run ? `${fmtUsd(run.invested)} · ${run.investments}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Free-exit period (days)">
          <input className={input} value={cool} onChange={(e) => /^\d{0,2}$/.test(e.target.value) && setCool(e.target.value)} />
        </Field>
        <Field label="Bonus cap per investment (USDT)">
          <NumInput className={input} value={cap} onValue={setCap} />
        </Field>
        <Field label="Maximum per investment (USDT)">
          <NumInput className={input} value={max} onValue={setMax} />
        </Field>
        <Field label="Cap on money in it (USDT, empty = none)">
          <NumInput className={input} value={aum} onValue={setAum} />
        </Field>
      </div>
      {p.aumCap && (
        <div className="flex flex-col gap-1">
          <div className="flex justify-between text-[12px] text-muted">
            <span>
              Invested {fmtUsd(p.invested)} of {fmtUsd(p.aumCap)}
            </span>
            <span className="num">{fmt(D.from(p.invested).div(p.aumCap).mul(100), 0)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-raised">
            <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.min(100, D.from(p.invested).div(p.aumCap).mul(100).toNumber())}%` }} />
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={primary} disabled={busy} onClick={() => void save()}>
          Save lock terms and limits
        </button>
        <span className="text-[12px] text-muted">
          Locked now {fmtUsd(total.sub(p.locks.byTerm.find((t) => t.days === 0)?.invested ?? "0"))} · bonuses paid in 30 days {fmtUsd(p.locks.bonusesPaid30d)}
          {D.from(p.locks.bonusesPending).isPos() ? ` · waiting for KYC ${fmtUsd(p.locks.bonusesPending)}` : ""} · early exits in 30 days {p.locks.earlyExits30d} (fees{" "}
          {fmtUsd(p.locks.exitFees30d)})
        </span>
      </div>
      {!!p.locks.dueIn30d.length && (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[520px] border-collapse text-[13px]">
            <thead>
              <tr className="bg-raised text-left text-[11.5px] text-muted">
                <th className="px-3 py-2 font-medium">Lock ends</th>
                <th className="px-3 py-2 font-medium">Investment</th>
                <th className="px-3 py-2 font-medium">Term</th>
                <th className="px-3 py-2 text-right font-medium">Put in</th>
                <th className="px-3 py-2 text-right font-medium">Bonus due</th>
              </tr>
            </thead>
            <tbody>
              {p.locks.dueIn30d.map((r) => (
                <tr key={r.investmentId} className="border-t border-line">
                  <td className="px-3 py-2">{fmtDate(r.lockedUntil)}</td>
                  <td className="num px-3 py-2 text-muted">{r.investmentId.slice(0, 8)}</td>
                  <td className="px-3 py-2">{lockLabel(r.days)}</td>
                  <td className="num px-3 py-2 text-right">{fmtUsd(r.invested)}</td>
                  <td className="num px-3 py-2 text-right text-up">{fmtUsd(r.bonus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------- new portfolio ----------

function NewPortfolio({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const [f, setF] = useState({
    name: "",
    slug: "",
    category: "crypto",
    tagline: "",
    minInvestment: "500",
    schedule: "monthly",
    scheduleDay: "1",
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<AdminPortfolio>("/api/admin/portfolios", {
        ...f,
        slug: f.slug || slugify(f.name),
        scheduleDay: Number(f.scheduleDay),
      });
      onDone(r.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not create");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title="New Smart Portfolio" width={480}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" className="col-span-2">
          <input
            className={input}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="AI Coins"
          />
        </Field>
        <Field label="Slug (URL)">
          <input
            className={input}
            value={f.slug}
            onChange={(e) => setF({ ...f, slug: e.target.value })}
            placeholder={slugify(f.name) || "ai-coins"}
          />
        </Field>
        <Field label="Category">
          <select
            className={input}
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
          >
            {PORTFOLIO_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tagline" className="col-span-2">
          <input
            className={input}
            value={f.tagline}
            maxLength={140}
            onChange={(e) => setF({ ...f, tagline: e.target.value })}
          />
        </Field>
        <Field label="Minimum (USDT)">
          <NumInput
            className={input}
            value={f.minInvestment}
            onValue={(v) => setF({ ...f, minInvestment: v })}
          />
        </Field>
        <Field label="Schedule">
          <div className="flex gap-1.5">
            <select
              className={input}
              value={f.schedule}
              onChange={(e) => setF({ ...f, schedule: e.target.value })}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="manual">Manual</option>
            </select>
            <input
              className={cx(input, "w-16")}
              aria-label="Day"
              value={f.scheduleDay}
              onChange={(e) =>
                /^\d{0,2}$/.test(e.target.value) &&
                setF({ ...f, scheduleDay: e.target.value })
              }
            />
          </div>
        </Field>
      </div>
      <p className="m-0 text-[12px] text-muted">
        It starts as a draft. Add the weights as version 1; once it is
        approved, the portfolio goes live.
      </p>
      {err && <p className="m-0 text-[13px] text-down">{err}</p>}
      <button
        type="button"
        disabled={busy || f.name.length < 2}
        onClick={() => void save()}
        className={primary}
      >
        {busy ? "Creating…" : "Create draft"}
      </button>
    </Modal>
  );
}

// ---------- versions ----------

function WeightsEditor({
  p,
  base,
  onClose,
  onDone,
}: {
  p: AdminPortfolio;
  base: PortfolioComponent[];
  onClose: () => void;
  onDone: () => void;
}) {
  const instruments = useInstruments().filter(
    (i) =>
      i.enabled && (i.assetClass as string) !== "MEME" && i.minLeverage <= 1,
  );
  const [rows, setRows] = useState<{ symbol: string; pct: string }[]>(
    base.length
      ? base.map((c) => ({ symbol: c.symbol, pct: String(c.weightBps / 100) }))
      : [
          { symbol: "", pct: "" },
          { symbol: "", pct: "" },
        ],
  );
  const [note, setNote] = useState("");
  const [effective, setEffective] = useState<"now" | "next_rebalance">("now");
  const [preview, setPreview] = useState<{
    problems: string[];
    points: { day: string; nav: number }[];
    return12m: string | null;
    maxDrawdown12m: string | null;
    riskScore: number | null;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const components: PortfolioComponent[] = rows
    .filter((r) => r.symbol)
    .map((r) => ({
      symbol: r.symbol,
      weightBps: Math.round(Number(r.pct || 0) * 100),
    }));
  const problem = weightsProblem(components);
  const sum = components.reduce((a, c) => a + c.weightBps, 0);
  const set = (i: number, patch: Partial<{ symbol: string; pct: string }>) =>
    setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const runPreview = async () => {
    setErr(null);
    try {
      setPreview(
        await api.post("/api/admin/portfolios/preview", {
          components,
          minInvestment: p.minInvestment,
        }),
      );
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Preview failed");
    }
  };
  const propose = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/admin/portfolios/${p.id}/versions`, {
        components,
        note,
        effective,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not propose");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`New weights for ${p.name}`}
      width={620}
    >
      <div className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              aria-label="Instrument"
              className={cx(input, "flex-1")}
              value={r.symbol}
              onChange={(e) => set(i, { symbol: e.target.value })}
            >
              <option value="">Choose an instrument…</option>
              {instruments.map((x) => (
                <option
                  key={x.symbol}
                  value={x.symbol}
                  disabled={rows.some(
                    (o, k) => k !== i && o.symbol === x.symbol,
                  )}
                >
                  {x.symbol} · {x.name}
                </option>
              ))}
            </select>
            <div className="flex w-[110px] items-center gap-1 rounded-lg border border-line bg-bg px-2">
              <NumInput
                aria-label="Weight"
                className="num h-9 w-full bg-transparent text-right text-[13px] outline-none"
                value={r.pct}
                onValue={(v) =>
                  /^\d{0,2}(\.\d{0,2})?$/.test(v) && set(i, { pct: v })
                }
              />
              <span className="text-muted">%</span>
            </div>
            <button
              type="button"
              aria-label="Remove"
              onClick={() => setRows(rows.filter((_, k) => k !== i))}
              className="h-9 w-9 rounded-lg text-muted hover:bg-raised hover:text-fg"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={btn}
          onClick={() => setRows([...rows, { symbol: "", pct: "" }])}
        >
          + Add holding
        </button>
        <button
          type="button"
          className={btn}
          disabled={!components.length}
          onClick={() => {
            const eq = equalWeights(components.map((c) => c.symbol));
            setRows(
              eq.map((c) => ({
                symbol: c.symbol,
                pct: String(c.weightBps / 100),
              })),
            );
          }}
        >
          Equal weights
        </button>
        <span
          className={cx(
            "num ml-auto text-[13px] font-semibold",
            sum === 10000 ? "text-up" : "text-warn",
          )}
        >
          Total {fmt(sum / 100, 2)}%
        </span>
      </div>
      {problem && components.length > 0 && (
        <p className="m-0 text-[12px] text-warn">{problem}</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Takes effect">
          <select
            className={input}
            value={effective}
            onChange={(e) =>
              setEffective(e.target.value as "now" | "next_rebalance")
            }
            disabled={p.status === "draft"}
          >
            <option value="now">
              {p.status === "draft"
                ? "When approved (publishes)"
                : "When approved (rebalance now)"}
            </option>
            <option value="next_rebalance">
              At the next scheduled rebalance
            </option>
          </select>
        </Field>
        <Field label="Note for investors (shown in the history)">
          <input
            className={input}
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>
      {preview && (
        <div className="rounded-lg border border-line p-3">
          {preview.problems.length ? (
            <ul className="m-0 pl-4 text-[12.5px] text-down">
              {preview.problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-4 text-[12.5px]">
                <span>
                  12M{" "}
                  <b className="num">
                    {preview.return12m === null
                      ? "—"
                      : `${fmt(D.from(preview.return12m).mul(100), 1)}%`}
                  </b>
                </span>
                <span>
                  Max drawdown{" "}
                  <b className="num">
                    {preview.maxDrawdown12m === null
                      ? "—"
                      : `${fmt(D.from(preview.maxDrawdown12m).mul(100), 1)}%`}
                  </b>
                </span>
                <RiskChip score={preview.riskScore} />
                <span className="text-muted">Simulated, monthly rebalance</span>
              </div>
              <NavChart
                points={preview.points.map((x) => ({
                  day: x.day,
                  nav: String(x.nav),
                  simulated: false,
                }))}
                height={180}
              />
            </>
          )}
        </div>
      )}
      {err && <p className="m-0 text-[13px] text-down">{err}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className={btn}
          disabled={!!problem}
          onClick={() => void runPreview()}
        >
          Back-test
        </button>
        <button
          type="button"
          className={primary}
          disabled={!!problem || busy}
          onClick={() => void propose()}
        >
          {busy ? "Sending…" : "Propose for approval"}
        </button>
      </div>
    </Modal>
  );
}

function VersionRow({
  v,
  current,
  onDecide,
}: {
  v: Version;
  current: boolean;
  onDecide: (approve: boolean) => void;
}) {
  return (
    <li className="flex flex-col gap-2 border-t border-line py-3 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-2">
        <b>Version {v.version}</b>
        <Pill s={v.status} />
        {current && (
          <span className="rounded-full bg-accent-bg px-2 py-0.5 text-[11px] font-semibold text-accent-fg">
            current
          </span>
        )}
        {v.status === "approved" && !v.effectiveAt && (
          <span className="text-[12px] text-warn">
            waits for the next rebalance
          </span>
        )}
        <span className="ml-auto text-[12px] text-muted">
          by {v.proposedBy} · {fmtDateTime(v.proposedAt)}
          {v.decidedBy
            ? ` · ${v.status === "rejected" ? "rejected" : "approved"} by ${v.decidedBy}`
            : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {v.components.map((c) => (
          <span
            key={c.symbol}
            className="num inline-flex items-center gap-1 rounded-full bg-raised py-0.5 pr-2 pl-1 text-[12px]"
          >
            <InstrumentLabel
              symbol={c.symbol}
              size={16}
              className="gap-1 font-sans font-bold"
            />
            {fmt(c.weightBps / 100, 2).replace(/\.00$/, "")}%
          </span>
        ))}
      </div>
      {v.note && <p className="m-0 text-[12.5px] text-muted">{v.note}</p>}
      {v.status === "proposed" && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={primary}
            onClick={() => onDecide(true)}
          >
            Approve
            {v.effective === "next_rebalance" ? " (at next rebalance)" : ""}
          </button>
          <button
            type="button"
            className={btn}
            onClick={() => onDecide(false)}
          >
            Reject
          </button>
        </div>
      )}
    </li>
  );
}

// ---------- one portfolio ----------

function PortfolioAdmin({
  id,
  onChanged,
}: {
  id: string;
  onChanged: () => void;
}) {
  const { data: p, reload } = useLoad<AdminPortfolio>(
    `/api/admin/portfolios/${id}`,
  );
  const runs = useLoad<{ runs: Run[] }>(`/api/admin/portfolios/${id}/runs`);
  const [edit, setEdit] = useState<Partial<AdminPortfolio>>({});
  const [weights, setWeights] = useState(false);
  const [dry, setDry] = useState<DryRun | null>(null);
  const [retire, setRetire] = useState(false);
  const [retireForm, setRetireForm] = useState({
    noticeDays: "30",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const current = useMemo(
    () =>
      p?.versions.find((v) => v.id === p.currentVersionId)?.components ?? [],
    [p],
  );

  const act = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await f();
      if (ok) setMsg({ ok: true, text: ok });
      await Promise.all([reload(), runs.reload()]);
      onChanged();
      return true;
    } catch (e) {
      setMsg({
        ok: false,
        text: e instanceof ApiError ? e.message : "Something went wrong",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!p)
    return (
      <div className="grid place-items-center py-10 text-muted">
        <Spinner />
      </div>
    );
  const published = ["live", "closed_to_new", "retiring"].includes(p.status);
  const val = <K extends keyof AdminPortfolio>(k: K) =>
    (edit[k] ?? p[k]) as AdminPortfolio[K];
  const dirty = Object.keys(edit).length > 0;

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-[17px] font-semibold">{p.name}</h3>
        <Pill s={p.status} />
        <span className="text-[12px] text-muted">/portfolios/{p.slug}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          {(p.status === "live" || p.status === "closed_to_new") && (
            <button
              type="button"
              className={btn}
              disabled={busy}
              onClick={() =>
                void act(() =>
                  api.post(`/api/admin/portfolios/${p.id}/new-investments`, {
                    open: p.status !== "live",
                    reason:
                      p.status === "live"
                        ? "Paused from Admin"
                        : "Reopened from Admin",
                  }),
                )
              }
            >
              {p.status === "live"
                ? "Pause new investments"
                : "Take new investments"}
            </button>
          )}
          {published && (
            <button
              type="button"
              className={btn}
              disabled={busy}
              onClick={() =>
                void act(async () =>
                  setDry(
                    (
                      await api.post<{ dryRun: DryRun }>(
                        `/api/admin/portfolios/${p.id}/rebalance`,
                        { dryRun: true },
                      )
                    ).dryRun,
                  ),
                )
              }
            >
              Rebalance now…
            </button>
          )}
          {!p.retire && p.status !== "retired" && p.status !== "retiring" && (
            <button
              type="button"
              className={cx(btn, "border-down text-down")}
              disabled={busy}
              onClick={() => setRetire(true)}
            >
              Retire…
            </button>
          )}
        </div>
      </div>
      {msg && (
        <p
          className={cx(
            "m-0 rounded-lg px-3 py-2 text-[13px]",
            msg.ok ? "bg-up-bg text-up" : "bg-down-bg text-down",
          )}
        >
          {msg.text}
        </p>
      )}

      {p.retire && p.status !== "retiring" && p.status !== "retired" && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-warn-bg px-3 py-2 text-[13px] text-warn">
          <span className="flex-1">
            {p.retire.requestedBy} asked to retire this portfolio (
            {p.retire.noticeDays} days' notice): “{p.retire.reason}”.
          </span>
          <button
            type="button"
            className={primary}
            disabled={busy}
            onClick={() =>
              void act(() =>
                api.post(`/api/admin/portfolios/${p.id}/retire/decision`, {
                  approve: true,
                }),
              )
            }
          >
            Approve retiring
          </button>
          <button
            type="button"
            className={btn}
            disabled={busy}
            onClick={() =>
              void act(() =>
                api.post(`/api/admin/portfolios/${p.id}/retire/decision`, {
                  approve: false,
                }),
              )
            }
          >
            Cancel
          </button>
        </div>
      )}
      {p.status === "retiring" && p.retire?.retireAt && (
        <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-[13px] text-down">
          Retiring: investments close on {fmtDateTime(p.retire.retireAt)}.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 text-[13px] sm:grid-cols-6">
        {[
          ["Investors", String(p.investors)],
          ["Invested", fmtUsd(p.invested)],
          ["In · 30d", fmtUsd(p.inflow30d)],
          ["Out · 30d", fmtUsd(p.outflow30d)],
          [
            "Next rebalance",
            p.nextRebalanceAt ? fmtDate(p.nextRebalanceAt) : "—",
          ],
          ["Risk", p.riskScore === null ? "—" : String(p.riskScore)],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg bg-raised px-3 py-2">
            <div className="text-[11.5px] text-muted">{k}</div>
            <div className="num font-semibold">{v}</div>
          </div>
        ))}
      </div>

      <section className="flex flex-col gap-3">
        <h4 className="m-0 text-[14px] font-semibold">Settings</h4>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Name">
            <input
              className={input}
              value={val("name")}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            />
          </Field>
          <Field label="Category">
            <select
              className={input}
              value={val("category")}
              onChange={(e) => setEdit({ ...edit, category: e.target.value })}
            >
              {PORTFOLIO_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Minimum (USDT)">
            <NumInput
              className={input}
              value={val("minInvestment")}
              onValue={(v) => setEdit({ ...edit, minInvestment: v })}
            />
          </Field>
          <Field label="Benchmark">
            <input
              className={input}
              value={val("benchmarkSymbol") ?? ""}
              placeholder="BTC"
              onChange={(e) =>
                setEdit({
                  ...edit,
                  benchmarkSymbol: e.target.value.toUpperCase(),
                })
              }
            />
          </Field>
          <Field label="Schedule">
            <select
              className={input}
              value={val("schedule")}
              onChange={(e) => setEdit({ ...edit, schedule: e.target.value })}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="manual">Manual</option>
            </select>
          </Field>
          <Field label="On day (1–28)">
            <input
              className={input}
              value={String(val("scheduleDay"))}
              onChange={(e) =>
                /^\d{0,2}$/.test(e.target.value) &&
                setEdit({ ...edit, scheduleDay: Number(e.target.value) })
              }
            />
          </Field>
          <Field label="Drift band (pp)">
            <NumInput
              className={input}
              value={val("driftPp")}
              onValue={(v) => setEdit({ ...edit, driftPp: v })}
            />
          </Field>
          <Field label="Default stop loss (%)">
            <NumInput
              className={input}
              value={
                edit.defaultStopLossPct !== undefined
                  ? edit.defaultStopLossPct
                  : String(Math.round(Number(p.defaultStopLossPct) * 100))
              }
              onValue={(v) => setEdit({ ...edit, defaultStopLossPct: v })}
            />
          </Field>
          <Field label="Tagline" className="col-span-2 sm:col-span-4">
            <input
              className={input}
              value={val("tagline")}
              maxLength={140}
              onChange={(e) => setEdit({ ...edit, tagline: e.target.value })}
            />
          </Field>
          <Field label="Description" className="col-span-2">
            <textarea
              className={cx(input, "h-24 py-2")}
              value={val("description")}
              maxLength={2000}
              onChange={(e) =>
                setEdit({ ...edit, description: e.target.value })
              }
            />
          </Field>
          <Field label="Strategy (shown to clients)" className="col-span-2">
            <textarea
              className={cx(input, "h-24 py-2")}
              value={val("strategy")}
              maxLength={2000}
              onChange={(e) => setEdit({ ...edit, strategy: e.target.value })}
            />
          </Field>
          <Field
            label="Image URL (https, optional)"
            className="col-span-2 sm:col-span-4"
          >
            <input
              className={input}
              value={val("imageUrl") ?? ""}
              onChange={(e) => setEdit({ ...edit, imageUrl: e.target.value })}
            />
          </Field>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={primary}
            disabled={!dirty || busy}
            onClick={() =>
              void act(async () => {
                const body = {
                  ...edit,
                  ...(edit.defaultStopLossPct !== undefined
                    ? {
                        defaultStopLossPct: D.from(
                          edit.defaultStopLossPct || "0",
                        )
                          .div(100)
                          .toString(),
                      }
                    : {}),
                };
                await api.patch(`/api/admin/portfolios/${p.id}`, body);
                setEdit({});
              }, "Saved")
            }
          >
            Save settings
          </button>
          {dirty && (
            <button type="button" className={btn} onClick={() => setEdit({})}>
              Discard
            </button>
          )}
        </div>
      </section>

      <LockLimits key={p.id} p={p} act={act} busy={busy} />

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h4 className="m-0 text-[14px] font-semibold">Weights (versions)</h4>
          {p.status !== "retired" && p.status !== "retiring" && (
            <button
              type="button"
              className={btn}
              disabled={p.versions.some((v) => v.status === "proposed")}
              onClick={() => setWeights(true)}
            >
              Propose new weights
            </button>
          )}
        </div>
        {!p.versions.length ? (
          <p className="m-0 text-[13px] text-muted">
            No weights yet. Propose version 1; approving it publishes the
            portfolio.
          </p>
        ) : (
          <ul className="m-0 list-none p-0">
            {p.versions.map((v) => (
              <VersionRow
                key={v.id}
                v={v}
                current={v.id === p.currentVersionId}
                onDecide={(approve) =>
                  void act(
                    () =>
                      api.post(
                        `/api/admin/portfolio-versions/${v.id}/decision`,
                        { approve },
                      ),
                    approve
                      ? p.status === "draft"
                        ? "Approved: the portfolio is live"
                        : "Approved"
                      : "Rejected",
                  )
                }
              />
            ))}
          </ul>
        )}
      </section>

      {published && (
        <section className="flex flex-col gap-2">
          <h4 className="m-0 text-[14px] font-semibold">Rebalance runs</h4>
          {!runs.data?.runs.length ? (
            <p className="m-0 text-[13px] text-muted">No runs yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="num w-full min-w-[640px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="text-left font-sans text-[11.5px] text-muted">
                    <th className="py-1.5 font-medium">Started</th>
                    <th className="py-1.5 font-medium">Trigger</th>
                    <th className="py-1.5 font-medium">Status</th>
                    <th className="py-1.5 text-right font-medium">v</th>
                    <th className="py-1.5 text-right font-medium">
                      Investments
                    </th>
                    <th className="py-1.5 text-right font-medium">Done</th>
                    <th className="py-1.5 text-right font-medium">Deferred</th>
                    <th className="py-1.5 text-right font-medium">Skipped</th>
                    <th className="py-1.5 text-right font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.data.runs.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="py-1.5">{fmtDateTime(r.startedAt)}</td>
                      <td className="py-1.5 font-sans">
                        {r.trigger}
                        {r.scopeInvestmentId ? (
                          <span className="text-muted"> · one investment</span>
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="py-1.5">
                        <Pill s={r.status} />
                      </td>
                      <td className="py-1.5 text-right">{r.version}</td>
                      <td className="py-1.5 text-right">
                        {r.stats.investments ?? 0}
                      </td>
                      <td className="py-1.5 text-right">{r.stats.done ?? 0}</td>
                      <td
                        className={cx(
                          "py-1.5 text-right",
                          r.deferredNow ? "text-warn" : "",
                        )}
                      >
                        {r.stats.deferred ?? 0}
                      </td>
                      <td className="py-1.5 text-right">
                        {r.stats.skipped ?? 0}
                      </td>
                      <td
                        className={cx(
                          "py-1.5 text-right",
                          r.stats.failed ? "text-down" : "",
                        )}
                      >
                        {r.stats.failed ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {weights && (
        <WeightsEditor
          p={p}
          base={current}
          onClose={() => setWeights(false)}
          onDone={() => {
            setWeights(false);
            void reload();
          }}
        />
      )}

      {dry && (
        <Modal
          open
          onClose={() => setDry(null)}
          title={`Rebalance ${p.name}?`}
          width={520}
        >
          <p className="m-0 text-[13px] text-muted">
            Dry run: what a rebalance of all {dry.investments.length} investment
            {dry.investments.length === 1 ? "" : "s"} would trade now. Clients
            buying means the house sells.
          </p>
          {!dry.bySymbol.length ? (
            <p className="m-0 text-[13px]">
              Every investment is on target: nothing to trade.
            </p>
          ) : (
            <table className="num w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left font-sans text-[11.5px] text-muted">
                  <th className="py-1.5 font-medium">Instrument</th>
                  <th className="py-1.5 text-right font-medium">Clients buy</th>
                  <th className="py-1.5 text-right font-medium">
                    Clients sell
                  </th>
                  <th className="py-1.5 text-right font-medium">Net</th>
                </tr>
              </thead>
              <tbody>
                {dry.bySymbol.map((s) => (
                  <tr key={s.symbol} className="border-t border-line">
                    <td className="py-1.5 font-sans font-semibold">
                      <InstrumentLabel symbol={s.symbol} />
                    </td>
                    <td className="py-1.5 text-right">{fmtUsd(s.buy)}</td>
                    <td className="py-1.5 text-right">{fmtUsd(s.sell)}</td>
                    <td
                      className={cx(
                        "py-1.5 text-right",
                        D.from(s.net).isNeg() ? "text-down" : "text-up",
                      )}
                    >
                      {fmtUsd(s.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className={btn} onClick={() => setDry(null)}>
              Cancel
            </button>
            <button
              type="button"
              className={primary}
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api.post(`/api/admin/portfolios/${p.id}/rebalance`, {
                    dryRun: false,
                  });
                  setDry(null);
                }, "Rebalance done")
              }
            >
              {busy ? "Rebalancing…" : "Rebalance now"}
            </button>
          </div>
        </Modal>
      )}

      {retire && (
        <Modal
          open
          onClose={() => setRetire(false)}
          title={`Retire ${p.name}?`}
          width={440}
        >
          <p className="m-0 text-[13px] text-muted">
            {p.status === "draft"
              ? "Nobody has invested in a draft; it is retired straight away."
              : "It needs an approval below. Investors are told once it is approved, and their investments close when the notice period ends."}
          </p>
          {p.status !== "draft" && (
            <Field label="Notice (days, 0–90)">
              <input
                className={input}
                value={retireForm.noticeDays}
                onChange={(e) =>
                  /^\d{0,2}$/.test(e.target.value) &&
                  setRetireForm({ ...retireForm, noticeDays: e.target.value })
                }
              />
            </Field>
          )}
          <Field label="Reason">
            <input
              className={input}
              value={retireForm.reason}
              maxLength={500}
              onChange={(e) =>
                setRetireForm({ ...retireForm, reason: e.target.value })
              }
            />
          </Field>
          <button
            type="button"
            className={cx(primary, "bg-down")}
            disabled={busy || !retireForm.reason.trim()}
            onClick={() =>
              void act(
                async () => {
                  await api.post(`/api/admin/portfolios/${p.id}/retire`, {
                    noticeDays: Number(retireForm.noticeDays || 0),
                    reason: retireForm.reason.trim(),
                  });
                  setRetire(false);
                },
                p.status === "draft"
                  ? "Retired"
                  : "Asked to retire: approve it to start the notice period",
              )
            }
          >
            {p.status === "draft" ? "Retire" : "Ask to retire"}
          </button>
        </Modal>
      )}
    </div>
  );
}

// ---------- tab ----------

export function SmartPortfoliosTab() {
  const { data, error, reload } = useLoad<{ portfolios: AdminPortfolio[] }>(
    "/api/admin/portfolios",
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = data?.portfolios ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 max-w-[70ch] text-[13px] text-muted">
          Themed portfolios run by the investment team. Weights change only
          through approved versions. Clients see a portfolio
          once version 1 is approved, and only while Smart Portfolios is
          switched on in Features.
        </p>
        <button
          type="button"
          className={primary}
          onClick={() => setCreating(true)}
        >
          New portfolio
        </button>
      </div>
      {error && <p className="m-0 text-down">{error}</p>}
      {!data ? (
        <div className="grid place-items-center py-10 text-muted">
          <Spinner />
        </div>
      ) : !list.length ? (
        <p className="m-0 rounded-2xl border border-line bg-panel p-6 text-center text-muted">
          No portfolios yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-panel">
          <table className="num w-full min-w-[820px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left font-sans text-[11.5px] text-muted">
                <th className="px-4 py-2.5 font-medium">Portfolio</th>
                <th className="py-2.5 font-medium">Status</th>
                <th className="py-2.5 text-right font-medium">Version</th>
                <th className="py-2.5 text-right font-medium">Investors</th>
                <th className="py-2.5 text-right font-medium">Invested</th>
                <th className="py-2.5 text-right font-medium">In / out 30d</th>
                <th className="py-2.5 pr-4 text-right font-medium">
                  Next rebalance
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const cur = p.versions.find((v) => v.id === p.currentVersionId);
                const pending = p.versions.find((v) => v.status === "proposed");
                return (
                  <tr
                    key={p.id}
                    onClick={() => setSelected(selected === p.id ? null : p.id)}
                    className={cx(
                      "cursor-pointer border-t border-line hover:bg-raised",
                      selected === p.id && "bg-raised",
                    )}
                  >
                    <td className="px-4 py-2.5 font-sans">
                      <b>{p.name}</b>{" "}
                      <span className="text-[12px] text-muted">{p.slug}</span>
                    </td>
                    <td className="py-2.5">
                      <span className="flex flex-wrap gap-1">
                        <Pill s={p.status} />
                        {pending && <Pill s="proposed" />}
                        {p.retire &&
                          p.status !== "retiring" &&
                          p.status !== "retired" && <Pill s="retiring" />}
                      </span>
                    </td>
                    <td className="py-2.5 text-right">
                      {cur ? `v${cur.version}` : "—"}
                    </td>
                    <td className="py-2.5 text-right">{p.investors}</td>
                    <td className="py-2.5 text-right">{fmtUsd(p.invested)}</td>
                    <td className="py-2.5 text-right">
                      {fmtUsd(p.inflow30d)} / {fmtUsd(p.outflow30d)}
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      {p.nextRebalanceAt ? fmtDate(p.nextRebalanceAt) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <PortfolioAdmin
          key={selected}
          id={selected}
          onChanged={() => void reload()}
        />
      )}
      {creating && (
        <NewPortfolio
          onClose={() => setCreating(false)}
          onDone={(id) => {
            setCreating(false);
            void reload();
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}
